import { CommandBarButton } from '@fluentui/react/lib/Button';
import { useWindow } from '@fluentui/react/lib/WindowProvider';
import { observer } from 'mobx-react-lite';
import type { ClientItemId } from 'owa-client-ids';
import { ControlIcons } from 'owa-control-icons';
import { isFolderOrSubFolderOfMsgRoot } from 'owa-folders';
import loc from 'owa-localize';
import { block } from 'owa-locstrings/lib/strings/block.locstring.json';
import { createRule } from 'owa-locstrings/lib/strings/createrule.locstring.json';
import { deleteItem } from 'owa-locstrings/lib/strings/deleteitem.locstring.json';
import { flag } from 'owa-locstrings/lib/strings/flag.locstring.json';
import { itemHeaderForward } from 'owa-locstrings/lib/strings/itemheaderforward.locstring.json';
import { itemHeaderReply } from 'owa-locstrings/lib/strings/itemheaderreply.locstring.json';
import { itemHeaderReplyAll } from 'owa-locstrings/lib/strings/itemheaderreplyall.locstring.json';
import { junk } from 'owa-locstrings/lib/strings/junk.locstring.json';
import { mailEllipsisAriaLabelText } from 'owa-locstrings/lib/strings/mailellipsisarialabeltext.locstring.json';
import { markAsRead } from 'owa-locstrings/lib/strings/markasread.locstring.json';
import { markAsUnread } from 'owa-locstrings/lib/strings/markasunread.locstring.json';
import { notjunk } from 'owa-locstrings/lib/strings/notjunk.locstring.json';
import { phishing } from 'owa-locstrings/lib/strings/phishing.locstring.json';
import { print as print_1 } from 'owa-locstrings/lib/strings/print.locstring.json';
import { unflag } from 'owa-locstrings/lib/strings/unflag.locstring.json';
import { closeDeeplinkReadingPane } from 'owa-mail-actions/lib/readingPaneActions';
import { lazyCreateRuleFromItem } from 'owa-mail-inbox-rules';
import isInboxRuleEnabledByAdmin from 'owa-mail-inbox-rules/lib/selectors/isInboxRuleEnabledByAdmin';
import * as markReadActions from 'owa-mail-mark-read-actions';
import { lazyForwardItem, lazyReplyToItem } from 'owa-mail-message-actions';
import checkItemReplyForwardDisabled from 'owa-mail-reading-pane-item-actions/lib/utils/checkItemReplyForwardDisabled';
import print from 'owa-mail-reading-pane-store/lib/actions/print';
import getDefaultDisposalType from 'owa-mail-reading-pane-store/lib/utils/getDefaultDisposalType';
import isReplyAllTheDefaultResponse from 'owa-mail-reading-pane-store/lib/utils/isReplyAllTheDefaultResponse';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import getItemRightsManagementRestrictions from 'owa-mail-store/lib/utils/getItemRightsManagementRestrictions';
import { useComputed } from 'owa-react-hooks/lib/useComputed';
import type Item from 'owa-service/lib/contract/Item';
import type Message from 'owa-service/lib/contract/Message';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import doesFolderIdEqualName from 'owa-session-store/lib/utils/doesFolderIdEqualName';
import doesMessageHaveSmimePrefix from 'owa-smime/lib/utils/doesMessageHaveSmimePrefix';
import * as React from 'react';
import {
    FilterableCommandBar,
    DisplayOption,
    FilterableMenuItem,
    FilterableMenuItemArgs,
} from 'owa-filterable-menu';
import {
    MenuItemType,
    isJunkEmailEnabledByAdmin,
    doesUserHaveActionPermission,
    getUserPermissionForFolderId,
} from 'owa-mail-filterable-menu-behavior';
import {
    getSelectedTableView,
    getSelectedTableViewId,
    shouldSuppressServerMarkReadOnReplyOrForward,
} from 'owa-mail-list-store';

import {
    lazyBlockLastSenderFromReadingPane,
    lazyDeleteItems,
    lazyMarkItemAsPhishingFromReadingPane,
    lazyMarkItemJunkNotJunkFromReadingPane,
    lazySetItemsFlagStateFromItemIds,
} from 'owa-mail-triage-action';

import styles from './MailCommandingBar.scss';
import classNames from 'classnames';
const deepLinkCommandBarActionSource = 'CommandBar';

export interface DeeplinkMailItemCommandingBarProps {
    itemId: ClientItemId;
}

export default observer(function DeeplinkMailItemCommandingBar(
    props: DeeplinkMailItemCommandingBarProps
) {
    const targetWindow = useWindow();

    const item = useComputed(
        (): Item => {
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2322 (85,13): Type 'ClientItem | undefined' is not assignable to type 'Item'.
            // @ts-expect-error
            return getMailStore().items.get(props.itemId.Id);
        }
    );
    const shouldDisablePrint = useComputed((): boolean => {
        const irmRestrictions = getItemRightsManagementRestrictions(item.get());
        return irmRestrictions ? !irmRestrictions.PrintAllowed : false;
    });
    const isInJunkFolder = useComputed((): boolean => {
        const message: Message = item.get();
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2345 (97,48): Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
        // @ts-expect-error
        return message ? doesFolderIdEqualName(message.ParentFolderId?.Id, 'junkemail') : false;
    });
    const isGroupItem = useComputed((): boolean => {
        return props.itemId.mailboxInfo?.type == 'GroupMailbox';
    });
    const canDelete = useComputed((): boolean => {
        if (!isGroupItem.get()) {
            return true;
        }
        const message: Message = item.get();
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (110,9): Type 'boolean | undefined' is not assignable to type 'boolean'.
        // @ts-expect-error
        return message ? message.CanDelete : false;
    });
    const replyMenuItem = useComputed(
        (): FilterableMenuItem => {
            const isReplyAll = isReplyAllTheDefaultResponse();
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.icon]: isReplyAll
                    ? ControlIcons.ReplyAll
                    : ControlIcons.Reply,
                [FilterableMenuItemArgs.menuContent]: [],
                [FilterableMenuItemArgs.key]: isReplyAll ? 'replyAllMain' : 'replyMain',
                [FilterableMenuItemArgs.onRender]: onRenderReplySplitButton,
            });
        }
    );
    const deleteMenuItem = useComputed(
        (): FilterableMenuItem => {
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(deleteItem),
                [FilterableMenuItemArgs.icon]: ControlIcons.Delete,
                [FilterableMenuItemArgs.onClick]: onDeleteClicked,
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Left,
                [FilterableMenuItemArgs.disabled]: !canDelete.get(), // Disable if item cannot be deleted
            });
        }
    );
    const junkMenuItem = useComputed(
        (): FilterableMenuItem => {
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(junk),
                [FilterableMenuItemArgs.icon]: ControlIcons.Blocked,
                [FilterableMenuItemArgs.onClick]: onJunkNotJunkClicked,
                [FilterableMenuItemArgs.shouldShow]: () =>
                    !isGroupItem.get() && !isInJunkFolder.get(),
            });
        }
    );
    const notJunkMenuItem = useComputed(
        (): FilterableMenuItem => {
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(notjunk),
                [FilterableMenuItemArgs.shouldShow]: () =>
                    !isGroupItem.get() && isInJunkFolder.get(),
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Left,
                [FilterableMenuItemArgs.menuContent]: [
                    new FilterableMenuItem({
                        [FilterableMenuItemArgs.name]: loc(notjunk),
                        [FilterableMenuItemArgs.onClick]: onJunkNotJunkClicked,
                    }),
                    new FilterableMenuItem({
                        [FilterableMenuItemArgs.name]: loc(phishing),
                        [FilterableMenuItemArgs.onClick]: onPhishingClicked,
                    }),
                ],
            });
        }
    );
    const blockMenuItem = useComputed(
        (): FilterableMenuItem => {
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(block),
                [FilterableMenuItemArgs.onClick]: onBlockClicked,
                [FilterableMenuItemArgs.shouldShow]: () => !isGroupItem.get(),
            });
        }
    );
    const markAsUnreadMenuItem = useComputed(
        (): FilterableMenuItem => {
            const message: Message = item.get();
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(markAsUnread),
                [FilterableMenuItemArgs.onClick]: onMarkAsUnreadClicked,
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2418 (185,17): Type of computed property's value is '() => boolean | undefined', which is not assignable to type 'ShouldShowBehavior'.
                // @ts-expect-error
                [FilterableMenuItemArgs.shouldShow]: () => !isGroupItem.get() && message?.IsRead,
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
            });
        }
    );
    const markAsReadMenuItem = useComputed(
        (): FilterableMenuItem => {
            const message: Message = item.get();
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(markAsRead),
                [FilterableMenuItemArgs.onClick]: onMarkAsReadClicked,
                [FilterableMenuItemArgs.shouldShow]: () => !isGroupItem.get() && !message?.IsRead,
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
            });
        }
    );
    const flagMenuItem = useComputed(
        (): FilterableMenuItem => {
            const message: Message = item.get();
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(flag),
                [FilterableMenuItemArgs.onClick]: onFlagClicked,
                [FilterableMenuItemArgs.shouldShow]: () =>
                    !isGroupItem.get() && message?.Flag?.FlagStatus != 'Flagged',
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
            });
        }
    );
    const unflagMenuItem = useComputed(
        (): FilterableMenuItem => {
            const message: Message = item.get();
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(unflag),
                [FilterableMenuItemArgs.onClick]: onUnFlagClicked,
                [FilterableMenuItemArgs.shouldShow]: () =>
                    !isGroupItem.get() && message?.Flag?.FlagStatus == 'Flagged',
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
            });
        }
    );
    const createRuleMenuItem = useComputed(
        (): FilterableMenuItem => {
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(createRule),
                [FilterableMenuItemArgs.onClick]: onCreateRuleClicked,
                [FilterableMenuItemArgs.shouldShow]: () => !isGroupItem.get(),
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
            });
        }
    );
    const printMenuItem = useComputed(
        (): FilterableMenuItem => {
            const message: Message = item.get();
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(print_1),
                [FilterableMenuItemArgs.onClick]: onPrintClicked,
                [FilterableMenuItemArgs.displayOption]: doesMessageHaveSmimePrefix(message)
                    ? DisplayOption.Left
                    : DisplayOption.Overflow,
                [FilterableMenuItemArgs.disabled]: shouldDisablePrint.get(),
            });
        }
    );
    const commandingBarItems = {
        get(): FilterableMenuItem[] {
            const message: Message = item.get();
            let menuItems;
            // When the item attachment is created by the S/MIME control, we show limited actions
            if (doesMessageHaveSmimePrefix(message)) {
                return [replyMenuItem.get(), printMenuItem.get()];
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2345 (258,53): Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
                // @ts-expect-error
            } else if (isFolderOrSubFolderOfMsgRoot(message?.ParentFolderId?.Id)) {
                // Items to display in the Display Bar for primary mailbox.
                menuItems = [
                    replyMenuItem.get(),
                    deleteMenuItem.get(),
                    isJunkEmailEnabledByAdmin() ? junkMenuItem.get() : null,
                    isJunkEmailEnabledByAdmin() ? notJunkMenuItem.get() : null,
                    blockMenuItem.get(),
                    markAsUnreadMenuItem.get(),
                    markAsReadMenuItem.get(),
                    flagMenuItem.get(),
                    unflagMenuItem.get(),
                    isInboxRuleEnabledByAdmin() ? createRuleMenuItem.get() : null,
                    printMenuItem.get(),
                ];
            } else {
                // Items to display in the Display Bar for mailboxes other than primary mailbox.
                const folderPermission = getUserPermissionForFolderId(
                    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                    // -> Error TS2345 (279,21): Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
                    // @ts-expect-error
                    message?.ParentFolderId?.Id,
                    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                    // -> Error TS2532 (283,21): Object is possibly 'undefined'.
                    // @ts-expect-error
                    getUserConfiguration().SessionSettings.UserEmailAddress
                );
                // For archive mailbox folderPermission will always be null and the doesUserHaveActionPermission utility will return true
                menuItems = [
                    doesUserHaveActionPermission(MenuItemType.Delete, folderPermission)
                        ? deleteMenuItem.get()
                        : null,
                    markAsUnreadMenuItem.get(),
                    markAsReadMenuItem.get(),
                    doesUserHaveActionPermission(MenuItemType.Flag, folderPermission)
                        ? flagMenuItem.get()
                        : null,
                    doesUserHaveActionPermission(MenuItemType.Unflag, folderPermission)
                        ? unflagMenuItem.get()
                        : null,
                    printMenuItem.get(),
                ];
            }
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2322 (304,13): Type '(FilterableMenuItem | null)[]' is not assignable to type 'FilterableMenuItem[]'.
            // @ts-expect-error
            return menuItems.filter(item => item != null);
        },
    };
    const onReplyToItem = () => {
        onReplyReplyAllToItemInternal(false /* isReplyAll */);
    };
    const onReplyAllToItem = () => {
        onReplyReplyAllToItemInternal(true /* isReplyAll */);
    };
    const onReplyReplyAllToItemInternal = (isReplyAll: boolean) => {
        lazyReplyToItem.importAndExecute(
            item.get().ItemId as ClientItemId,
            isReplyAll,
            deepLinkCommandBarActionSource,
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (321,13): Argument of type 'null' is not assignable to parameter of type 'InstrumentationContext[]'.
            // @ts-expect-error
            null,
            shouldSuppressServerMarkReadOnReplyOrForward(getSelectedTableView()),
            targetWindow
        );
    };
    const onForwardItem = () => {
        lazyForwardItem.importAndExecute(
            item.get().ItemId as ClientItemId,
            deepLinkCommandBarActionSource,
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (333,13): Argument of type 'null' is not assignable to parameter of type 'InstrumentationContext[]'.
            // @ts-expect-error
            null,
            shouldSuppressServerMarkReadOnReplyOrForward(getSelectedTableView()),
            targetWindow
        );
    };
    const onDeleteClicked = () => {
        lazyDeleteItems.import().then(deleteItems => {
            deleteItems(
                [props.itemId.Id],
                getDefaultDisposalType(props.itemId.Id),
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2345 (346,17): Argument of type 'null' is not assignable to parameter of type 'InstrumentationContext[]'.
                // @ts-expect-error
                null,
                deepLinkCommandBarActionSource,
                props.itemId.mailboxInfo
            ).then(() => {
                closeDeeplinkReadingPane();
            });
        });
    };
    const onJunkNotJunkClicked = () => {
        lazyMarkItemJunkNotJunkFromReadingPane
            .importAndExecute(
                props.itemId.Id,
                getSelectedTableView(),
                deepLinkCommandBarActionSource,
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2345 (363,17): Argument of type 'null' is not assignable to parameter of type 'InstrumentationContext[]'.
                // @ts-expect-error
                null /*instrumentationContext*/,
                targetWindow
            )
            .then(() => {
                closeDeeplinkReadingPane();
            });
    };
    const onPhishingClicked = () => {
        lazyMarkItemAsPhishingFromReadingPane
            .importAndExecute(
                props.itemId.Id,
                getSelectedTableView(),
                deepLinkCommandBarActionSource,
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2345 (379,17): Argument of type 'Window | undefined' is not assignable to parameter of type 'Window'.
                // @ts-expect-error
                targetWindow
            )
            .then(() => {
                closeDeeplinkReadingPane();
            });
    };
    const onBlockClicked = () => {
        lazyBlockLastSenderFromReadingPane.importAndExecute(
            props.itemId.Id,
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (391,13): Argument of type 'null' is not assignable to parameter of type 'InstrumentationContext[]'.
            // @ts-expect-error
            null /*instrumentationContexts*/,
            null /*actionSource*/,
            targetWindow
        );
    };
    const onMarkAsReadClicked = () => {
        markReadActions.lazyMarkItemAsReadFromReadingPane.importAndExecute(
            props.itemId.Id,
            getSelectedTableView(),
            true /* isReadValue */,
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (404,13): Argument of type 'null' is not assignable to parameter of type 'InstrumentationContext[]'.
            // @ts-expect-error
            null,
            deepLinkCommandBarActionSource
        );
    };
    const onMarkAsUnreadClicked = () => {
        markReadActions.lazyMarkItemAsReadFromReadingPane.importAndExecute(
            props.itemId.Id,
            getSelectedTableView(),
            false /* isReadValue */,
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (416,13): Argument of type 'null' is not assignable to parameter of type 'InstrumentationContext[]'.
            // @ts-expect-error
            null,
            deepLinkCommandBarActionSource
        );
    };
    const onFlagClicked = () => {
        lazySetItemsFlagStateFromItemIds.importAndExecute(
            [props.itemId.Id],
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (426,13): Argument of type 'null' is not assignable to parameter of type 'InstrumentationContext[]'.
            // @ts-expect-error
            null,
            { FlagStatus: 'Flagged' },
            getSelectedTableViewId(),
            deepLinkCommandBarActionSource
        );
    };
    const onUnFlagClicked = () => {
        lazySetItemsFlagStateFromItemIds.importAndExecute(
            [props.itemId.Id],
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (438,13): Argument of type 'null' is not assignable to parameter of type 'InstrumentationContext[]'.
            // @ts-expect-error
            null,
            { FlagStatus: 'NotFlagged' },
            getSelectedTableViewId(),
            deepLinkCommandBarActionSource
        );
    };
    /**
     * Called when user clicks on the create rule menu item
     */
    const onCreateRuleClicked = async () => {
        const message = item.get() as Message;
        const createRuleFromItem = await lazyCreateRuleFromItem.import();
        createRuleFromItem(message, 'DeepLinkCommandBar', targetWindow);
    };
    const onPrintClicked = () => {
        print(
            {
                mailboxInfo: props.itemId.mailboxInfo,
                Id: props.itemId.Id,
            },
            deepLinkCommandBarActionSource,
            targetWindow
        );
    };
    const onRenderReplySplitButton = () => {
        const isReplyAll = isReplyAllTheDefaultResponse();
        const [
            isReplyDisabled,
            isReplyAllDisabled,
            isForwardDisabled,
        ] = checkItemReplyForwardDisabled(item.get(), false /*isConversationItemPart*/);
        const primaryDisabled = isReplyAll ? isReplyAllDisabled : isReplyDisabled;
        const shouldDisable = isReplyAllDisabled && isReplyDisabled && isForwardDisabled;
        const fluentReplyButtonStyles = {
            splitButtonMenuIcon: styles.replyButtonContent,
            splitButtonContainer: classNames(
                styles.replyButtonContainerFluent,
                primaryDisabled && styles.disabledReplyButtonContent
            ),
            splitButtonMenuButton: classNames(
                styles.replyButtonMenuButtonFluent,
                shouldDisable && styles.disabledReplyButtonContent
            ),
            root: styles.replyButtonRootFluent,
            label: styles.replyButtonContent,
            icon: styles.replyButtonContent,
        };
        return (
            <CommandBarButton
                iconProps={isReplyAll ? { iconName: 'ReplyAll' } : { iconName: 'Reply' }}
                primaryDisabled={primaryDisabled}
                disabled={shouldDisable}
                text={isReplyAll ? loc(itemHeaderReplyAll) : loc(itemHeaderReply)}
                onClick={isReplyAll ? onReplyAllToItem : onReplyToItem}
                split={true}
                styles={fluentReplyButtonStyles}
                menuProps={{
                    items: [
                        {
                            key: 'reply',
                            name: loc(itemHeaderReply),
                            onClick: onReplyToItem,
                            disabled: isReplyDisabled,
                        },
                        {
                            key: 'replyAll',
                            name: loc(itemHeaderReplyAll),
                            onClick: onReplyAllToItem,
                            disabled: isReplyAllDisabled,
                        },
                        {
                            key: 'forward',
                            name: loc(itemHeaderForward),
                            onClick: onForwardItem,
                            disabled: isForwardDisabled,
                        },
                    ],
                }}
            />
        );
    };
    return (
        <FilterableCommandBar
            filterableItems={commandingBarItems.get()}
            elipisisAriaLabel={loc(mailEllipsisAriaLabelText)}
            styles={{ root: styles.commandBarRoot }}
        />
    );
});
